import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Sparkles, 
  Mail, 
  Lock, 
  User, 
  Building2, 
  Briefcase,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Zap,
  Users,
  BarChart3
} from 'lucide-react';

const Login = () => {
  const { login, register, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  
  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });
  
  // Register form state
  const [registerForm, setRegisterForm] = useState({
    companyName: '',
    companySector: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Redirect if already authenticated
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginForm.email || !loginForm.password) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('¡Bienvenido de vuelta!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al iniciar sesión';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!registerForm.companyName || !registerForm.firstName || 
        !registerForm.lastName || !registerForm.email || !registerForm.password) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    try {
      await register({
        companyName: registerForm.companyName,
        companySector: registerForm.companySector,
        firstName: registerForm.firstName,
        lastName: registerForm.lastName,
        email: registerForm.email,
        password: registerForm.password
      });
      toast.success('¡Cuenta creada exitosamente!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al registrar';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Zap,
      title: 'IA Integrada',
      description: 'Mejora textos y diseños automáticamente con inteligencia artificial'
    },
    {
      icon: Users,
      title: 'Gestión de Contactos',
      description: 'Importa y organiza tu base de datos de suscriptores'
    },
    {
      icon: BarChart3,
      title: 'Campañas Efectivas',
      description: 'Crea emails profesionales que generan resultados'
    }
  ];

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Features */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-64 h-64 bg-indigo-500 rounded-full blur-[100px]" />
          <div className="absolute bottom-40 right-20 w-80 h-80 bg-pink-500 rounded-full blur-[120px]" />
        </div>
        
        {/* Content */}
        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-16">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-2xl text-white font-[Manrope]">MarketFlow</h1>
              <span className="text-sm text-slate-400">v2.0</span>
            </div>
          </div>
          
          {/* Hero text */}
          <div className="mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white font-[Manrope] leading-tight mb-6">
              Marketing automation
              <span className="bg-gradient-to-r from-indigo-400 to-pink-400 bg-clip-text text-transparent"> potenciado por IA</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-md">
              Crea campañas de email profesionales en minutos con inteligencia artificial y un editor visual intuitivo.
            </p>
          </div>
          
          {/* Features */}
          <div className="space-y-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-indigo-500/30 transition-colors">
                  <feature.icon className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">{feature.title}</h3>
                  <p className="text-sm text-slate-400">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer */}
        <div className="relative z-10">
          <p className="text-sm text-slate-500">
            © 2026 MarketFlow. Sistema de marketing automation.
          </p>
        </div>
      </div>
      
      {/* Right side - Auth forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-bold text-xl text-slate-900 font-[Manrope]">MarketFlow</h1>
          </div>
          
          <Card className="border-0 shadow-xl shadow-slate-200/50">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold font-[Manrope]">
                {activeTab === 'login' ? 'Bienvenido de vuelta' : 'Crear cuenta'}
              </CardTitle>
              <CardDescription>
                {activeTab === 'login' 
                  ? 'Ingresa tus credenciales para continuar' 
                  : 'Registra tu empresa para comenzar'}
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">
                    Iniciar sesión
                  </TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">
                    Registrarse
                  </TabsTrigger>
                </TabsList>
                
                {/* Login Form */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="tu@email.com"
                          className="pl-10"
                          value={loginForm.email}
                          onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Contraseña</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10"
                          value={loginForm.password}
                          onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                          data-testid="login-password-input"
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full ai-button text-white" 
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <ArrowRight className="w-4 h-4 mr-2" />
                      )}
                      {loading ? 'Ingresando...' : 'Ingresar'}
                    </Button>
                  </form>
                </TabsContent>
                
                {/* Register Form */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                    {/* Company info */}
                    <div className="space-y-4 pb-4 border-b border-slate-100">
                      <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Datos de la empresa
                      </p>
                      
                      <div className="space-y-2">
                        <Label htmlFor="company-name">Nombre de empresa *</Label>
                        <Input
                          id="company-name"
                          placeholder="Mi Empresa S.L."
                          value={registerForm.companyName}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, companyName: e.target.value }))}
                          data-testid="register-company-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="company-sector">Sector</Label>
                        <div className="relative">
                          <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="company-sector"
                            placeholder="E-commerce, SaaS, etc."
                            className="pl-10"
                            value={registerForm.companySector}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, companySector: e.target.value }))}
                            data-testid="register-sector-input"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* User info */}
                    <div className="space-y-4 pb-4 border-b border-slate-100">
                      <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Datos del administrador
                      </p>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label htmlFor="first-name">Nombre *</Label>
                          <Input
                            id="first-name"
                            placeholder="Juan"
                            value={registerForm.firstName}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, firstName: e.target.value }))}
                            data-testid="register-firstname-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="last-name">Apellido *</Label>
                          <Input
                            id="last-name"
                            placeholder="García"
                            value={registerForm.lastName}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, lastName: e.target.value }))}
                            data-testid="register-lastname-input"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Credentials */}
                    <div className="space-y-4">
                      <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        Credenciales de acceso
                      </p>
                      
                      <div className="space-y-2">
                        <Label htmlFor="register-email">Email *</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="register-email"
                            type="email"
                            placeholder="admin@miempresa.com"
                            className="pl-10"
                            value={registerForm.email}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                            data-testid="register-email-input"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="register-password">Contraseña *</Label>
                        <Input
                          id="register-password"
                          type="password"
                          placeholder="Mínimo 6 caracteres"
                          value={registerForm.password}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, password: e.target.value }))}
                          data-testid="register-password-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password">Confirmar contraseña *</Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          placeholder="Repite la contraseña"
                          value={registerForm.confirmPassword}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          data-testid="register-confirm-password-input"
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full ai-button text-white mt-2" 
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      )}
                      {loading ? 'Creando cuenta...' : 'Crear cuenta'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
          
          {/* Help text */}
          <p className="text-center text-sm text-slate-500 mt-6">
            {activeTab === 'login' ? (
              <>
                ¿No tienes cuenta?{' '}
                <button 
                  onClick={() => setActiveTab('register')}
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Regístrate gratis
                </button>
              </>
            ) : (
              <>
                ¿Ya tienes cuenta?{' '}
                <button 
                  onClick={() => setActiveTab('login')}
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Inicia sesión
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
